import React, { useEffect, useMemo, useState } from "react";
import { loadManifest, DocIndex } from "../../docs/loader";
import { Link, useLocation } from "react-router-dom";
import Fuse from "fuse.js";
import i18n from "../../i18n";

export type SidebarNavProps = {
  onNavigate?: () => void;
};

const SidebarNav: React.FC<SidebarNavProps> = ({ onNavigate }) => {
  const [items, setItems] = useState<DocIndex[]>([]);
  const [query, setQuery] = useState("");
  const location = useLocation();

  useEffect(() => {
    loadManifest().then(setItems).catch(console.error);
    const onLang = () => {
      loadManifest().then(setItems).catch(console.error);
    };
    i18n.on("languageChanged", onLang);
    return () => {
      i18n.off("languageChanged", onLang);
    };
  }, []);

  const filtered = useMemo(() => {
    if (!query) return items;
    const fuse = new Fuse(items, {
      threshold: 0.35,
      ignoreLocation: true,
      keys: ["title", "section", "slug", "meta.summary", "meta.tags"],
    });
    return fuse.search(query).map((r) => r.item);
  }, [items, query]);

  const grouped = useMemo(() => {
    const map: Record<string, DocIndex[]> = {};
    for (const it of filtered) {
      if (!map[it.section]) map[it.section] = [];
      map[it.section].push(it);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return map;
  }, [filtered]);

  const activePath = location.pathname.replace(/^\/[^/]+\/docs\/?/, "");

  return (
    <div className="p-4">
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search docs…"
        className="w-full mb-4 px-3 py-2 rounded border bg-white dark:bg-gray-900"
      />
      <nav className="space-y-4">
        {Object.keys(grouped).map((section) => (
          <div key={section}>
            <Link
              to={`/${i18n.language.split("-")[0]}/docs/${section}/index`}
              onClick={onNavigate}
              className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1 hover:underline inline-block"
            >
              {section.replace(/-/g, " ")}
            </Link>
            <ul className="space-y-1">
              {grouped[section].map((item) => {
                const isActive = item.slug === activePath;
                return (
                  <li key={item.slug}>
                    <Link
                      to={`/${i18n.language.split("-")[0]}/docs/${item.slug}`}
                      onClick={onNavigate}
                      className={`block px-2 py-1 rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                        isActive
                          ? "bg-gray-100 dark:bg-gray-800 font-medium"
                          : ""
                      }`}
                    >
                      {item.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </div>
  );
};

export default SidebarNav;
